/* eslint-disable lingui/text-restrictions */

import { isFeatureEnabled } from "@/client/constants/feature-flags";
import { DEFAULT_MAX_TOKENS, DEFAULT_MODEL } from "@/client/constants/llm";
import { axios } from "@/client/libs/axios";
import { useOpenAiStore } from "@/client/stores/openai";

import { openai } from "./client";
import { getOptimalModel } from "./model-selector";

const PROMPT = `You are an AI specialized in writing professional resume summaries.

⚠️ CRITICAL LANGUAGE REQUIREMENT ⚠️
YOU MUST WRITE THE ENTIRE SUMMARY IN {language}. THIS IS NON-NEGOTIABLE.
DO NOT WRITE A SINGLE WORD IN ENGLISH IF THE TARGET LANGUAGE IS NOT ENGLISH.

LANGUAGE RULES:
- Target Language: {language}
- If {language} is "Arabic": Write everything in Arabic script (العربية)
- If {language} is "Spanish": Write everything in Spanish
- If {language} is "French": Write everything in French
- If {language} is any other language: Write everything in that language
- NO ENGLISH WORDS OR PHRASES ALLOWED (unless target language is English)

TASK: Generate a professional summary for a {jobTitle} position that is:
- Written ENTIRELY in {language} with proper grammar
- 4-6 sentences long
- Highlighting key skills and qualifications for this role
- Focused on achievements and professional attributes
- Using professional terminology common in {language} resumes
- Culturally appropriate for {language} speaking markets

Job Title: "{jobTitle}"
Target Language: "{language}"

REMINDER: Write the professional summary now in {language}. Do not use any English words if the target language is not English.

Professional Summary in {language}:`;

export const generateSummary = async (jobTitle: string, language = "English") => {
  // ✅ FIXED: Removed t macro from default parameter
  const { model, maxTokens, useSystemKey } = useOpenAiStore.getState();

  // Debug logging
  console.log(`generateSummary called with jobTitle: "${jobTitle}", language: "${language}"`);

  // If using system API, send request to our backend AI gateway
  if (useSystemKey) {
    try {
      console.log(`Sending to backend: { jobTitle: "${jobTitle}", language: "${language}" }`);
      const response = await axios.post("/ai/write/summary", { jobTitle, language });
      // Return the text directly as that's what the backend sends
      return response.data || "";
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      throw new Error(`Failed to generate summary: ${errorMessage}`);
    }
  }

  // Otherwise use original implementation with direct OpenAI client
  const prompt = PROMPT.replace(/{jobTitle}/g, jobTitle).replace(/{language}/g, language);

  // Debug: Log the final constructed prompt
  console.log("=== CLIENT-SIDE GENERATED PROMPT ===");
  console.log(prompt);
  console.log("=== END CLIENT-SIDE PROMPT ===");

  // Get optimal model if feature flag is enabled (upgrade to gpt-4o for better summaries)
  const useOptimizedModels = isFeatureEnabled("OPTIMIZED_AI_MODELS");
  const modelConfig = useOptimizedModels
    ? getOptimalModel("summary")
    : {
        model: model ?? DEFAULT_MODEL,
        maxTokens: maxTokens ?? DEFAULT_MAX_TOKENS,
        temperature: 0.7,
      };

  // Explicitly resolve the openai() promise to fix the typing issue
  const client = await openai();
  const result = await client.chat.completions.create({
    messages: [{ role: "user", content: prompt }],
    model: modelConfig.model,
    max_tokens: modelConfig.maxTokens,
    temperature: modelConfig.temperature,
    n: 1,
  });

  if (result.choices.length === 0) {
    throw new Error("OpenAI did not return any choices for your request."); // ✅ FIXED: Removed t macro
  }

  return result.choices[0].message.content ?? "";
};
